import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Bike, CalendarDays, ClipboardList, ListOrdered, Wrench, Warehouse, Boxes, ShoppingCart, Receipt,
  Wallet, BadgeDollarSign, ShieldCheck, Settings, Store, MessageCircle, Bot, ScrollText, BarChart3, LogOut, Menu, X, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_URL, api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Modal } from "@/components/shared";
import { PinInput } from "@/components/GoogleButton";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: ["dashboard.read"] },
  { to: "/mekanik", label: "WO Saya", icon: Wrench, perm: ["wo.read_assigned"], mobile: true },
  { to: "/antrean", label: "Antrean", icon: ListOrdered, perm: ["wo.read"], mobile: true },
  { to: "/work-orders", label: "Work Order", icon: ClipboardList, perm: ["wo.read"], mobile: true },
  { to: "/booking", label: "Booking", icon: CalendarDays, perm: ["wo.read"] },
  { to: "/kasir", label: "Kasir", icon: Receipt, perm: ["payment.capture"], mobile: true },
  { to: "/gudang", label: "Gudang", icon: Warehouse, perm: ["inventory.issue"], mobile: true },
  { to: "/pelanggan", label: "Pelanggan", icon: Users, perm: ["customer.read"] },
  { to: "/kendaraan", label: "Kendaraan", icon: Bike, perm: ["customer.read"] },
  { to: "/inventori", label: "Inventori", icon: Boxes, perm: ["inventory.read"], mobile: true },
  { to: "/pembelian", label: "Pembelian", icon: ShoppingCart, perm: ["purchasing.manage", "purchasing.receive"] },
  { to: "/keuangan", label: "Keuangan", icon: Wallet, perm: ["finance.read"] },
  { to: "/payroll", label: "Payroll & Komisi", icon: BadgeDollarSign, perm: ["payroll.manage", "commission.read_own"], mobile: true },
  { to: "/marketplace", label: "Marketplace", icon: Store, perm: ["marketplace.read"] },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, perm: ["notifications.read"] },
  { to: "/ai", label: "AI Assistant", icon: Bot, perm: ["ai.use"] },
  { to: "/laporan", label: "Laporan", icon: BarChart3, perm: ["reports.read"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, perm: ["audit.read"] },
  { to: "/pengguna", label: "Pengguna & Akses", icon: ShieldCheck, perm: ["users.manage", "users.read"] },
  { to: "/pengaturan", label: "Pengaturan & Integrasi", icon: Settings, perm: ["settings.manage", "integrations.read"] },
];

export const Logo = ({ className }) => <img src="/logo.png" alt="MJM Workshop" className={cn("object-contain", className)} data-testid="mjm-logo" />;

export default function AppLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = NAV.filter((n) => can(...n.perm));

  useEffect(() => {
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (m) => {
        const { event, payload } = JSON.parse(m.data);
        if (event === "part.requested" && can("inventory.issue")) toast(`Permintaan part: ${payload.product_name} x${payload.qty}`, { description: "Gudang" });
        if (event === "stock.low" && can("inventory.read")) toast.warning(`Stok rendah: ${payload.name} (${payload.stock})`);
        if (event === "approval.requested" && can("wo.read")) toast(`Menunggu persetujuan pelanggan`, { description: payload.wo_id });
        if (event === "booking.created" && can("wo.read")) toast(`Booking baru ${payload.plate || ""} (${payload.date})`, { description: payload.source === "WA" ? "via WhatsApp" : "manual" });
        if (event === "marketplace.short" && can("inventory.read")) toast.warning(`Order marketplace stok kurang: ${payload.items.join(", ")}`);
        if (event === "payment.captured" && can("finance.read")) toast.success(`Pembayaran ${payload.invoice_number} diterima`);
        window.dispatchEvent(new CustomEvent("mjm-event", { detail: { event, payload } }));
      };
    } catch { /* ignore */ }
    return () => ws && ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Nav = ({ onClick }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5" data-testid="sidebar-nav">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onClick} data-testid={`nav-${n.to.slice(1)}`} className={({ isActive }) => cn("nav-item", isActive && "active")}>
          <n.icon className="h-4 w-4 shrink-0" /><span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen mjm-bg mjm-grain text-zinc-100 flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-zinc-800/80 bg-[#0c0c10]/90 backdrop-blur sticky top-0 h-screen z-10">
        <div className="px-5 py-5 border-b border-zinc-800/80"><Logo className="h-12" /></div>
        <Nav />
        <UserBox user={user} onLogout={doLogout} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-[#0c0c10] border-r border-zinc-800 flex flex-col" data-testid="mobile-drawer">
            <div className="px-5 py-4 flex items-center justify-between border-b border-zinc-800"><Logo className="h-10" /><button onClick={() => setOpen(false)} data-testid="drawer-close-btn"><X className="h-5 w-5" /></button></div>
            <Nav onClick={() => setOpen(false)} />
            <UserBox user={user} onLogout={doLogout} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col relative z-[1]">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-[#0c0c10]/95 backdrop-blur border-b border-zinc-800">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-1"><Menu className="h-5 w-5" /></button>
          <Logo className="h-8" />
          <div className="h-8 w-8 rounded-full bg-red-800 flex items-center justify-center text-xs font-bold" data-testid="header-user-initial">{user?.name?.[0]}</div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 lg:pb-8 max-w-[1600px] w-full mx-auto"><Outlet /></main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0c0c10]/95 backdrop-blur border-t border-zinc-800 flex justify-around py-1.5" data-testid="mobile-bottom-nav">
          {items.filter((n) => n.mobile).slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`bottom-nav-${n.to.slice(1)}`} className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-2 py-1 text-[10px]", isActive ? "text-red-400" : "text-zinc-500")}>
              <n.icon className="h-5 w-5" />{n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

const UserBox = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-zinc-800/80 p-4 flex items-center gap-3" data-testid="sidebar-user">
      {user?.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-red-800 flex items-center justify-center font-bold">{user?.name?.[0]}</div>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-[11px] text-red-400 font-mono tracking-wider" data-testid="sidebar-user-role">{user?.role}</div>
      </div>
      {user?.username && <button onClick={() => setOpen(true)} data-testid="change-pin-btn" className="text-zinc-500 hover:text-white transition-colors" title="Ganti PIN"><KeyRound className="h-4 w-4" /></button>}
      <button onClick={onLogout} data-testid="logout-btn" className="text-zinc-500 hover:text-red-400 transition-colors" title="Keluar"><LogOut className="h-4 w-4" /></button>
      <ChangePinModal open={open} onClose={() => setOpen(false)} user={user} />
    </div>
  );
};

const ChangePinModal = ({ open, onClose, user }) => {
  const [f, setF] = useState({ old_pin: "", new_pin: "", new_pin2: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_pin !== f.new_pin2) return toast.error("Konfirmasi PIN tidak sama");
    try { await api.post("/auth/change-pin", { old_pin: f.old_pin || null, new_pin: f.new_pin }); toast.success("PIN diganti"); setF({ old_pin: "", new_pin: "", new_pin2: "" }); onClose(); }
    catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Ganti PIN" testId="change-pin-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-zinc-400">Username: <span className="font-mono text-red-400">@{user?.username}</span></div>
        {user?.has_pin && <div><div className="text-xs text-zinc-400 uppercase tracking-wider mb-1.5">PIN lama</div><PinInput value={f.old_pin} onChange={(v) => setF({ ...f, old_pin: v })} testId="change-pin-old" placeholder="PIN lama" /></div>}
        <div className="grid grid-cols-2 gap-3">
          <div><div className="text-xs text-zinc-400 uppercase tracking-wider mb-1.5">PIN baru</div><PinInput value={f.new_pin} onChange={(v) => setF({ ...f, new_pin: v })} testId="change-pin-new" placeholder="min. 4 karakter" /></div>
          <div><div className="text-xs text-zinc-400 uppercase tracking-wider mb-1.5">Ulangi</div><PinInput value={f.new_pin2} onChange={(v) => setF({ ...f, new_pin2: v })} testId="change-pin-new2" placeholder="Ulangi" /></div>
        </div>
        <button type="submit" disabled={f.new_pin.length < 4} data-testid="change-pin-submit" className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded py-2.5 font-semibold">Simpan PIN</button>
      </form>
    </Modal>
  );
};
